/* eslint-disable */
/**
 * AnalyticsPanel.jsx — Analytics: SEO + AEO + UTM (Variant A: "hvad vi ejer")
 *
 * Datakilder (verificerede endpoints under /api/os/website-analytics/):
 *   GET /api/os/website-analytics/rank-tracking  → { sogeord:[{sogeord,position,forige_position,url,tjekket_kl}], vigtige:[] }
 *   GET /api/os/website-analytics/llm-visibility → { resultater:[{llm,checks:[],sidst_tjekket}], rader:[] }
 *   GET /api/os/website-analytics/utm            → { links:[{id,navn,utm_source,utm_medium,utm_campaign,fuld_url,oprettet_kl}] }
 *
 * GA4 = Live via GET /api/os/website-analytics/ga4?periode=30d (fra_cache, opdateres hvert 60. min af scheduler).
 * Panelet crasher ALDRIG ved manglende data — viser TomTilstand per sektion.
 *
 * Design: Sager-stil, Sektioner med SammenklappeligSektion, mobil-kortliste.
 * Mobil-først: Android 360px viewport.
 */

import React from 'react';
import { Globe, BarChart3, Brain, Link2, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  DataTabel,
  SektionsHeader,
  SammenklappeligSektion,
  useApiPolling,
  useWindowWidth,
  BG, KANT, TEKST, STATUS, RADIUS, SPACING, TYPE, FONT,
} from '../komponenter/index';

// ─── Hjælpefunktioner ────────────────────────────────────────────────────────

function formatTid(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function positionFarve(pos) {
  if (pos == null) return STATUS.ikkeForbundet;
  if (pos <= 3)   return STATUS.live;
  if (pos <= 10)  return STATUS.konfigureret;
  if (pos <= 20)  return STATUS.opmærksomhed;
  return STATUS.fejl;
}

function trendPil(pos, forrige) {
  if (pos == null || forrige == null) return null;
  const diff = forrige - pos; // positivt = rykket op (lavere tal = bedre)
  if (diff > 0) return { pil: '↑', farve: STATUS.live, delta: diff };
  if (diff < 0) return { pil: '↓', farve: STATUS.fejl, delta: -diff };
  return null;
}

// ─── Mobil: SEO-kortliste ─────────────────────────────────────────────────────

function SeoKortListe({ rækker }) {
  if (!rækker || rækker.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rækker.map((r, i) => {
        const fv = positionFarve(r.position);
        const trend = trendPil(r.position, r.forige_position);
        return (
          <div
            key={r.sogeord ?? i}
            style={{
              background: BG.kort,
              border: `1px solid ${KANT.standard}`,
              borderLeft: `3px solid ${fv}`,
              borderRadius: RADIUS.kort,
              padding: '10px 12px',
              fontFamily: FONT.family,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.sogeord}
              </span>
              {/* Position badge */}
              <span style={{
                fontSize: 14, fontWeight: 800, color: fv,
                background: `${fv}18`, border: `1px solid ${fv}35`,
                borderRadius: RADIUS.badge, padding: '2px 8px', marginLeft: 8, flexShrink: 0,
              }}>
                {r.position != null ? `#${r.position}` : '?'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {trend ? (
                <span style={{ fontSize: 12, color: trend.farve, fontWeight: 700 }}>
                  {trend.pil} {trend.delta} pladser
                </span>
              ) : (
                <span style={{ fontSize: 11, color: TEKST.sekundær }}>Ingen ændring</span>
              )}
              <span style={{ fontSize: 11, color: TEKST.sekundær }}>{formatTid(r.tjekket_kl)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Mobil: LLM-kortliste ─────────────────────────────────────────────────────

function LlmKortListe({ resultater }) {
  if (!resultater || resultater.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {resultater.map((r, i) => {
        const checks = r.checks || [];
        const naevnerCount = checks.filter(c => c.naevner_sv).length;
        const pct = checks.length > 0 ? Math.round((naevnerCount / checks.length) * 100) : 0;
        const fv = pct >= 50 ? STATUS.live : pct >= 20 ? STATUS.opmærksomhed : STATUS.fejl;
        return (
          <div
            key={r.llm ?? i}
            style={{
              background: BG.kort,
              border: `1px solid ${KANT.standard}`,
              borderLeft: `3px solid ${fv}`,
              borderRadius: RADIUS.kort,
              padding: '10px 12px',
              fontFamily: FONT.family,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 700, textTransform: 'capitalize' }}>
                {r.llm}
              </span>
              <span style={{ fontSize: 16, fontWeight: 800, color: fv }}>{pct}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: 11, color: TEKST.sekundær }}>
                {naevnerCount}/{checks.length} checks nævner SkilsmisseVejen
              </span>
              <span style={{ fontSize: 11, color: TEKST.sekundær }}>
                {formatTid(r.sidst_tjekket)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Hoved-komponent ─────────────────────────────────────────────────────────

export default function AnalyticsPanel() {
  const bredde = useWindowWidth();
  const isMobil = bredde < 600;

  // Data polling
  const { data: rankData,  fejl: rankFejl  } = useApiPolling('/os/website-analytics/rank-tracking',  120000);
  const { data: llmData,   fejl: llmFejl   } = useApiPolling('/os/website-analytics/llm-visibility', 120000);
  const { data: utmData,   fejl: utmFejl   } = useApiPolling('/os/website-analytics/utm',            120000);
  const { data: ga4Data,   fejl: ga4Fejl   } = useApiPolling('/os/website-analytics/ga4?periode=30d', 300000);

  // Udpak
  const sogeord   = rankData?.sogeord || [];
  const vigtige   = rankData?.vigtige || [];
  const llmRes    = llmData?.resultater || [];
  const utmLinks  = utmData?.links || [];

  // GA4
  const ga4Trafik    = ga4Data?.data?.trafik;
  const ga4Sider     = ga4Data?.data?.sider;
  const ga4HentetKl  = ga4Data?.data?.hentet_kl;
  const ga4Sessions  = ga4Trafik?.rows
    ? ga4Trafik.rows.reduce((s, r) => s + parseInt(r.metricValues?.[0]?.value || 0), 0)
    : null;
  const ga4TopSider  = (ga4Sider?.rows || []).slice(0, 5).map(r => ({
    sti:   r.dimensionValues?.[0]?.value || '/',
    titel: r.dimensionValues?.[1]?.value || '—',
    visninger: parseInt(r.metricValues?.[0]?.value || 0),
  }));

  // KPI-beregninger
  const rankAntal    = sogeord.length;
  const sidsteLlm    = llmRes.length > 0
    ? llmRes.sort((a, b) => new Date(b.sidst_tjekket) - new Date(a.sidst_tjekket))[0]
    : null;
  const aktivUtm     = utmLinks.length;

  // Ranking-kolonner
  const rankKolonner = [
    {
      nøgle: 'sogeord',
      label: 'Søgeord',
      render: (v) => (
        <span style={{ ...TYPE.tabelCelle, color: TEKST.primær, fontWeight: 600 }}>{v}</span>
      ),
    },
    {
      nøgle: 'position',
      label: 'Position',
      render: (v, row) => {
        const fv = positionFarve(v);
        const trend = trendPil(v, row?.forige_position);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: 14, fontWeight: 800, color: fv,
              background: `${fv}15`, border: `1px solid ${fv}30`,
              borderRadius: RADIUS.badge, padding: '2px 7px',
            }}>
              {v != null ? `#${v}` : '?'}
            </span>
            {trend && (
              <span style={{ fontSize: 11, fontWeight: 700, color: trend.farve, display: 'flex', alignItems: 'center', gap: 2 }}>
                {trend.pil === '↑' ? <TrendingUp size={12} color={trend.farve} /> : <TrendingDown size={12} color={trend.farve} />}
                {trend.delta}
              </span>
            )}
          </div>
        );
      },
    },
    {
      nøgle: 'url',
      label: 'Side',
      render: (v) => v ? (
        <span style={{ fontSize: 11, color: TEKST.sekundær, wordBreak: 'break-all' }}>
          {v.replace('https://skilsmissevejen.dk', '')}
        </span>
      ) : <span style={{ color: TEKST.sekundær }}>—</span>,
    },
    {
      nøgle: 'tjekket_kl',
      label: 'Opdateret',
      render: (v) => <span style={{ fontSize: 11, color: TEKST.sekundær }}>{formatTid(v)}</span>,
    },
  ];

  // LLM-kolonner
  const llmKolonner = [
    {
      nøgle: 'llm',
      label: 'AI-søgemaskine',
      render: (v) => (
        <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 700, textTransform: 'capitalize' }}>{v}</span>
      ),
    },
    {
      nøgle: 'checks',
      label: 'Nævner SV',
      render: (v) => {
        const checks = v || [];
        const naevner = checks.filter(c => c.naevner_sv).length;
        const pct = checks.length > 0 ? Math.round((naevner / checks.length) * 100) : 0;
        const fv = pct >= 50 ? STATUS.live : pct >= 20 ? STATUS.opmærksomhed : STATUS.fejl;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              height: 6, width: 80, borderRadius: 3,
              background: 'rgba(255,255,255,0.08)',
              overflow: 'hidden',
            }}>
              <div style={{ height: '100%', width: `${pct}%`, background: fv, borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: fv }}>{pct}%</span>
          </div>
        );
      },
    },
    {
      nøgle: 'sidst_tjekket',
      label: 'Sidst tjekket',
      render: (v) => <span style={{ fontSize: 11, color: TEKST.sekundær }}>{formatTid(v)}</span>,
    },
  ];

  // UTM-kolonner
  const utmKolonner = [
    {
      nøgle: 'navn',
      label: 'Kampagnenavn',
      render: (v) => (
        <span style={{ fontSize: 13, color: TEKST.primær, fontWeight: 600 }}>{v || '—'}</span>
      ),
    },
    {
      nøgle: 'utm_source',
      label: 'Source',
      render: (v) => <span style={{ fontSize: 12, color: TEKST.sekundær }}>{v || '—'}</span>,
    },
    {
      nøgle: 'utm_medium',
      label: 'Medium',
      render: (v) => <span style={{ fontSize: 12, color: TEKST.sekundær }}>{v || '—'}</span>,
    },
    {
      nøgle: 'utm_campaign',
      label: 'Kampagne',
      render: (v) => <span style={{ fontSize: 12, color: TEKST.sekundær }}>{v || '—'}</span>,
    },
    {
      nøgle: 'oprettet_kl',
      label: 'Oprettet',
      render: (v) => <span style={{ fontSize: 11, color: TEKST.sekundær }}>{formatTid(v)}</span>,
    },
  ];

  return (
    // OS-08: overflowX:hidden på panelets rod-div forhindrer UTM-tabel i at
    // skabe 480px+ horisontal overflow på mobil. DataTabel-containere scroller internt.
    <div style={{ fontFamily: FONT.family, overflowX: 'hidden' }}>

      {/* Header */}
      <SektionsHeader
        titel="Analytics"
        undertitel="SEO-rankings, LLM-synlighed og UTM-links (egne data)"
        ikon={<BarChart3 size={16} />}
        badge="KONFIGURERET"
      />

      {/* KPI-rad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobil ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: SPACING.gap,
        marginBottom: SPACING.sektionGap,
      }}>
        <KpiKort
          label="Trackede søgeord"
          kortLabel="SØGEORD"
          value={rankFejl ? '?' : rankAntal > 0 ? rankAntal : (rankData ? 0 : undefined)}
          farve={rankFejl ? STATUS.fejl : STATUS.konfigureret}
          ikon={<Globe size={16} />}
          badge={rankFejl ? 'FEJL' : rankData ? 'LIVE' : 'IKKE FORBUNDET'}
          mobil={isMobil}
        />
        <KpiKort
          label="Seneste LLM-check"
          kortLabel="LLM"
          value={llmFejl ? '?' : sidsteLlm ? formatTid(sidsteLlm.sidst_tjekket) : (llmData ? '—' : undefined)}
          farve={llmFejl ? STATUS.fejl : sidsteLlm ? STATUS.ai : STATUS.ikkeForbundet}
          ikon={<Brain size={16} />}
          badge={llmFejl ? 'FEJL' : llmData ? 'LIVE' : 'IKKE FORBUNDET'}
          mobil={isMobil}
        />
        <KpiKort
          label="Aktive UTM-links"
          kortLabel="UTM"
          value={utmFejl ? '?' : aktivUtm > 0 ? aktivUtm : (utmData ? 0 : undefined)}
          farve={utmFejl ? STATUS.fejl : STATUS.konfigureret}
          ikon={<Link2 size={16} />}
          badge={utmFejl ? 'FEJL' : utmData ? 'LIVE' : 'IKKE FORBUNDET'}
          mobil={isMobil}
        />
        <KpiKort
          label="GA4 Sessioner (30d)"
          kortLabel="GA4"
          value={ga4Fejl ? '?' : ga4Sessions != null ? ga4Sessions.toLocaleString('da-DK') : (ga4Data ? '0' : undefined)}
          farve={ga4Fejl ? STATUS.fejl : ga4Sessions > 0 ? STATUS.live : ga4Data ? STATUS.opmærksomhed : STATUS.ikkeForbundet}
          ikon={<BarChart3 size={16} />}
          badge={ga4Fejl ? 'FEJL' : ga4Sessions != null ? 'LIVE' : 'HENTER'}
          mobil={isMobil}
        />
      </div>

      {/* Sektion 1: SEO-rankings */}
      <SammenklappeligSektion
        titel="Keyword-rankings (SEO)"
        defaultÅben={true}
        isMobil={isMobil}
        style={{ marginBottom: SPACING.sektionGap }}
      >
        {rankFejl ? (
          <TomTilstand
            titel="Ranking-data ikke tilgængeligt"
            besked={`GET /api/os/website-analytics/rank-tracking fejlede. ${rankFejl}`}
            ikon={<AlertCircle size={24} />}
            badge="FEJL"
          />
        ) : !rankData ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
            Henter ranking-data…
          </div>
        ) : sogeord.length === 0 ? (
          <TomTilstand
            titel="Ingen ranking-data endnu"
            besked="Kør 'Opdatér rankings' fra HjemmesidePanel for at hente Google Search Console-data."
            ikon={<Globe size={24} />}
            badge="KONFIGURERET"
          />
        ) : isMobil ? (
          <SeoKortListe rækker={sogeord} />
        ) : (
          <DataTabel
            kolonner={rankKolonner}
            rækker={sogeord}
            tomBesked="Ingen søgeord tracket"
          />
        )}
      </SammenklappeligSektion>

      {/* Sektion 2: LLM-synlighed (AEO) */}
      <SammenklappeligSektion
        titel="LLM-synlighed (AEO)"
        defaultÅben={true}
        isMobil={isMobil}
        style={{ marginBottom: SPACING.sektionGap }}
      >
        {llmFejl ? (
          <TomTilstand
            titel="LLM-data ikke tilgængeligt"
            besked={`GET /api/os/website-analytics/llm-visibility fejlede. ${llmFejl}`}
            ikon={<AlertCircle size={24} />}
            badge="FEJL"
          />
        ) : !llmData ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
            Henter LLM-data…
          </div>
        ) : llmRes.length === 0 ? (
          <TomTilstand
            titel="Ingen LLM-checks endnu"
            besked="LLM-synlighed checkes automatisk af analysesystemet. Ingen data de seneste 8 dage."
            ikon={<Brain size={24} />}
            badge="KONFIGURERET"
          />
        ) : isMobil ? (
          <LlmKortListe resultater={llmRes} />
        ) : (
          <DataTabel
            kolonner={llmKolonner}
            rækker={llmRes}
            tomBesked="Ingen LLM-data"
          />
        )}
      </SammenklappeligSektion>

      {/* Sektion 3: UTM-links */}
      <SammenklappeligSektion
        titel="UTM-links"
        defaultÅben={false}
        isMobil={isMobil}
        style={{ marginBottom: SPACING.sektionGap }}
      >
        {utmFejl ? (
          <TomTilstand
            titel="UTM-links ikke tilgængeligt"
            besked={`GET /api/os/website-analytics/utm fejlede. ${utmFejl}`}
            ikon={<AlertCircle size={24} />}
            badge="FEJL"
          />
        ) : !utmData ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
            Henter UTM-links…
          </div>
        ) : utmLinks.length === 0 ? (
          <TomTilstand
            titel="Ingen UTM-links oprettet"
            besked="Opret UTM-links fra Hjemmeside-panelet for at tracke kampagner."
            ikon={<Link2 size={24} />}
            badge="IKKE FORBUNDET"
          />
        ) : (
          <DataTabel
            kolonner={utmKolonner}
            rækker={utmLinks}
            tomBesked="Ingen UTM-links"
          />
        )}
      </SammenklappeligSektion>

      {/* GA4 — Top sider */}
      <SammenklappeligSektion
        titel="GA4 — Top sider (30d)"
        defaultÅben={false}
        isMobil={isMobil}
      >
        {ga4Fejl ? (
          <TomTilstand
            titel="GA4-data ikke tilgængeligt"
            besked={`GET /api/os/website-analytics/ga4 fejlede. ${ga4Fejl}`}
            ikon={<AlertCircle size={24} />}
            badge="FEJL"
          />
        ) : !ga4Data ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
            Henter GA4-data…
          </div>
        ) : ga4TopSider.length === 0 ? (
          <TomTilstand
            titel="Ingen GA4-data endnu"
            besked="GA4-data hentes automatisk hvert 60. minut af analytics-scheduleren."
            ikon={<BarChart3 size={24} />}
            badge="KONFIGURERET"
          />
        ) : (
          <>
            <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 12 }}>
              Opdateret: {formatTid(ga4HentetKl)} · Sessioner i alt: {ga4Sessions?.toLocaleString('da-DK') ?? '—'}
            </div>
            <DataTabel
              kolonner={[
                { nøgle: 'sti',   label: 'Side',      render: v => <span style={{ fontSize: 12, color: TEKST.primær, wordBreak: 'break-all' }}>{v.replace('https://skilsmissevejen.dk', '') || '/'}</span> },
                { nøgle: 'visninger', label: 'Visninger', render: v => <span style={{ fontSize: 13, fontWeight: 700, color: STATUS.live }}>{v.toLocaleString('da-DK')}</span> },
              ]}
              rækker={ga4TopSider}
              tomBesked="Ingen sidevisningsdata"
            />
          </>
        )}
      </SammenklappeligSektion>

    </div>
  );
}
